import { Hover, IconWrap, PencilIcon, Stack } from '@libs/ui/ds'
import { usePlanningTranslation } from '@libs/utils/translations'
import { getRestConfig } from '@spa/constants/RestTypes'
import { useMemoSelector } from '@spa/redux/hooks'
import { RestNote, Tube } from '@spa/scenes/Plannings/components'
import {
  restsSelectors,
  useGetTimeRange,
  useGetWeekDateParams
} from '@spa/scenes/Plannings/redux/selectors'
import cn from 'classnames'
import { isNumber } from 'lodash-es'
import React, { memo } from 'react'
import type { FC } from 'react'

import { getRestDurationInDays } from './helpers/getRestDurationInDays'
import getRoundedOption from './helpers/getRoundedOption'

type Props = {
  outlined?: boolean
  restId: number
  classNames?: {
    root?: string
    title?: string
  }
}
export const RestTube: FC<Props> = memo(
  ({ restId, outlined, classNames = {} }) => {
    const { t, tCommon } = usePlanningTranslation()
    const timeRange = useGetTimeRange()
    const { week } = useGetWeekDateParams()

    const {
      rest,
      restItem,
      rounded,
      revaluedDuration,
      isRevalued,
      fullDuration
    } = useMemoSelector(state => {
      const rest = restsSelectors.getCachedById(state, restId)
      const cValue = rest.custom_value?.find(
        entry => typeof entry[week.iso] === 'number'
      )
      const revaluedDuration = cValue ? cValue[week.iso] : undefined
      const isRevalued = isNumber(revaluedDuration)
      const restItem = getRestConfig(rest)
      const { fullDuration } = getRestDurationInDays({ rest })
      const rounded = getRoundedOption({
        restStart: rest.starts_at,
        restEnd: rest.ends_at,
        timeRange
      })

      return {
        rest,
        restItem,
        rounded,
        revaluedDuration,
        isRevalued,
        fullDuration
      }
    })
    const absenceName = rest.rest_type
      ? tCommon(`models.rests.${rest.rest_type}.name`)
      : tCommon(`models.rests.default_rest.name`)

    return (
      <Hover enabled={isRevalued} className={classNames.root}>
        <Tube
          outlined={outlined}
          backgroundColor={restItem.background_color}
          roundedLeft={rounded.left}
          roundedRight={rounded.right}
        >
          <Stack center gap={1} className='whitespace-normal'>
            <Hover.Idle className='flex min-w-0 grow truncate'>
              <span className={cn(classNames.title, 'truncate')}>
                {absenceName}{' '}
                {t('weekly.rests.durationInDays', { count: fullDuration })}
              </span>
            </Hover.Idle>

            {isRevalued && (
              <Hover.Hovered className={cn(classNames.title, 'truncate')}>
                {t('weekly.rests.revaluedRest', {
                  duration: revaluedDuration * 60
                })}
              </Hover.Hovered>
            )}
            {isRevalued && <IconWrap icon={<PencilIcon />} size='xs' />}
            <RestNote rest={rest} />
          </Stack>
        </Tube>
      </Hover>
    )
  }
)
